import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BackButton,
  Button,
  LocationCard,
  theme,
  useBreakpointHelpers,
  VStack,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useReorderState } from '../../../ReorderingScreen/hooks';
import type { SingleLocationProps } from '../../LocationScreen.types';
import {
  getLocationA11yProps,
  getUserDistance,
} from '../../LocationScreen.utils';
import { LocationDetailsMobile } from './SingleLocation.details';

export const SingleLocationResult = React.memo((props: SingleLocationProps) => {
  const { currentBreakpoint } = useBreakpointHelpers();

  return currentBreakpoint.isMobile ? (
    <MobileLayout {...props} />
  ) : (
    <DesktopLayout {...props} />
  );
});

export const MobileLayout = React.memo((props: SingleLocationProps) => {
  const {
    location,
    userLocation,
    isShowingLocationDetails,
    onCardPress,
    onInfoPress,
  } = props;
  const { t } = useLocalizationContext();
  const { handleOnBack, handleOnCallToAction } = useCallbacks(props);
  const { reordering, restaurantSlug } = useReorderState();
  const a11yProps = getLocationA11yProps(t, location, 'button');

  if (isShowingLocationDetails) {
    return (
      <LocationDetailsMobile
        {...props}
        handleOnBack={handleOnBack}
        handleOnCallToAction={handleOnCallToAction}
      />
    );
  }

  return (
    <View style={styles.content} testID="locationsearch.single-location">
      <LocationCard
        key={location?.id}
        isLoading={
          reordering ? restaurantSlug === location.restaurantSlug : undefined
        }
        distance={getUserDistance(userLocation.geolocation, location)}
        onCardPress={onCardPress}
        onInfoPress={isShowingLocationDetails ? undefined : onInfoPress}
        {...a11yProps}
        {...location}
      />
    </View>
  );
});

export const DesktopLayout = React.memo((props: SingleLocationProps) => {
  const { location, userLocation, isShowingLocationDetails, onCardPress } =
    props;
  const { handleOnBack, handleOnCallToAction } = useCallbacks(props);
  const { t } = useLocalizationContext();
  const a11yProps = getLocationA11yProps(t, location, 'button');
  const { reordering, restaurantSlug } = useReorderState();
  const viewMenuText = t(
    isShowingLocationDetails ? 'location.order-now' : 'location.view-menu',
  );

  return (
    <VStack style={styles.content} testID="locationsearch.single-location">
      <BackButton onPress={handleOnBack} />
      <LocationCard
        key={location?.id}
        isLoading={
          reordering ? restaurantSlug === location.restaurantSlug : undefined
        }
        distance={getUserDistance(userLocation.geolocation, location)}
        onCardPress={onCardPress}
        {...a11yProps}
        {...location}
      />
      <Button
        size="large"
        testID="locationsearch.view-menu"
        onPress={handleOnCallToAction}
      >
        {viewMenuText}
      </Button>
    </VStack>
  );
});

const useCallbacks = ({
  location,
  isShowingLocationDetails,
  setShowLocationDetails,
  setShouldShowSingleLocation,
  onCardPress,
}: SingleLocationProps) => {
  const handleOnBack = React.useCallback(() => {
    if (isShowingLocationDetails) setShowLocationDetails(false);
    else setShouldShowSingleLocation(false);
  }, [
    isShowingLocationDetails,
    setShowLocationDetails,
    setShouldShowSingleLocation,
  ]);

  const handleOnCallToAction = React.useCallback(() => {
    onCardPress({
      restaurantId: location?.id,
      restaurantSlug: location?.restaurantSlug,
      restaurantName: location?.name,
    });
  }, [location?.id, location?.restaurantSlug, location?.name, onCardPress]);

  return { handleOnBack, handleOnCallToAction };
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: theme.spacing.medium,
  },
});
